import React, { useState, useEffect } from 'react';
import { FiSearch, FiDownload, FiCalendar, FiFilter } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const StaffRiwayatTransaksi = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch transaction data from API
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const response = await API.get('/api/booking/payments/history', {
          params: {
            page: currentPage,
            status: filterStatus !== 'all' ? filterStatus : undefined,
            paymentStatus: filterPaymentStatus !== 'all' ? filterPaymentStatus : undefined,
            search: searchQuery,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo
          }
        });
        console.log('Fetched transaction history:', response.data);

        if (response.data && response.data.transactions) {
          setTransactions(response.data.transactions);
          setTotalPages(response.data.pagination?.totalPages || Math.ceil(response.data.transactions.length / 10));
        } else {
          const transactionList = response.data || [];
          setTransactions(transactionList);
          setTotalPages(Math.ceil(transactionList.length / 10) || 1);
        }
      } catch (error) {
        console.error('Error fetching transaction history:', error);
        setTransactions([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [currentPage, searchQuery, filterStatus, filterPaymentStatus, filterDateFrom, filterDateTo]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // Trigger search through useEffect
  };

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1);
  };

  const handlePaymentStatusChange = (e) => {
    setFilterPaymentStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFromChange = (e) => {
    setFilterDateFrom(e.target.value);
    setCurrentPage(1);
  };

  const handleDateToChange = (e) => {
    setFilterDateTo(e.target.value);
    setCurrentPage(1);
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getServiceCount = (transaction) => transaction?.service_items?.length || 0;

  const getBatchBadge = (transaction) => {
    const serviceCount = getServiceCount(transaction);

    if (serviceCount <= 1) {
      return null;
    }

    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
        Batch {serviceCount} layanan
      </span>
    );
  };

  const handleViewDetail = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTransaction(null);
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Diterima
        </span>
      );
    } else if (status === 'rejected') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Ditolak
        </span>
      );
    } else if (status === 'pending') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Menunggu
        </span>
      );
    }
    return null;
  };

  const getPaymentStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Lunas
        </span>
      );
    } else if (status === 'unpaid') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Belum Bayar
        </span>
      );
    } else if (status === 'partial') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Bayar Sebagian
        </span>
      );
    }
    return null;
  };

  const handleExportData = () => {
    alert('Fitur export data akan segera tersedia');
  };

  const renderPagination = () => {
    return (
      <div className="flex justify-center mt-6">
        <nav className="flex items-center">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Sebelumnya
          </button>
          <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-sm text-gray-700">
            Halaman {currentPage} dari {totalPages}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Berikutnya
          </button>
        </nav>
      </div>
    );
  };

  const renderDetailModal = () => {
    if (!selectedTransaction) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">Detail Transaksi</h3>
            <button
              onClick={closeDetailModal}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4">
            {/* Status Badge */}
            <div className="mb-4 flex justify-between items-center">
              <div>
                <div className="text-sm text-gray-500">Invoice</div>
                <div className="font-semibold">{selectedTransaction.invoice_number}</div>
                <div className="mt-2">{getBatchBadge(selectedTransaction)}</div>
              </div>
              <div className="flex gap-2">
                {getStatusBadge(selectedTransaction.status)}
                {getPaymentStatusBadge(selectedTransaction.payment_status)}
              </div>
            </div>

            {/* Transaction Info */}
            <div className="mb-4 border rounded-md p-3 bg-gray-50">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-sm text-gray-500">ID Booking</div>
                  <div className="font-semibold">{selectedTransaction.booking_id}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">ID Transaksi</div>
                  <div className="font-semibold">#{selectedTransaction.id}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Tanggal Servis</div>
                  <div className="font-medium">{selectedTransaction.service_date}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Tanggal Pembayaran</div>
                  <div className="font-medium">{selectedTransaction.payment_date}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Jumlah Layanan</div>
                  <div className="font-medium">{getServiceCount(selectedTransaction)} layanan</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Batch ID</div>
                  <div className="font-medium">{selectedTransaction.booking_batch_id || '-'}</div>
                </div>
              </div>
            </div>

            {/* Customer & Vehicle info */}
            <div className="mb-4 border rounded-md p-3 bg-gray-50">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Pelanggan</div>
                  <div className="font-medium">{selectedTransaction.customer_name}</div>
                  <div className="text-sm text-gray-600">{selectedTransaction.customer_phone}</div>
                  <div className="text-sm text-gray-600">{selectedTransaction.customer_email}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Kendaraan</div>
                  <div className="font-medium">{selectedTransaction.vehicle_make} {selectedTransaction.vehicle_model} ({selectedTransaction.vehicle_year})</div>
                  <div className="text-sm text-gray-600">Plat: {selectedTransaction.vehicle_plate}</div>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <div className="text-sm text-gray-500 mb-2">Item Servis & Sparepart</div>
              <div className="border rounded overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Harga
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedTransaction.service_items.map((item, index) => (
                      <tr key={index} className={item.type === 'other' ? 'bg-gray-50' : ''}>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          {item.name}
                          {item.type === 'service' && <span className="ml-1 text-xs text-blue-600">(Jasa)</span>}
                          {item.type === 'part' && <span className="ml-1 text-xs text-green-600">(Part)</span>}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500 text-center">
                          {item.qty}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 text-right">
                          {formatRupiah(item.price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td colSpan="2" className="px-3 py-2 text-xs font-medium text-gray-500">
                        Subtotal
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                        {formatRupiah(selectedTransaction.subtotal)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" className="px-3 py-2 text-xs font-medium text-gray-500">
                        Pajak (PPN 10%)
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                        {formatRupiah(selectedTransaction.tax)}
                      </td>
                    </tr>
                    <tr className="bg-gray-200">
                      <td colSpan="2" className="px-3 py-2 text-sm font-bold text-gray-700">
                        Total
                      </td>
                      <td className="px-3 py-2 text-sm font-bold text-gray-900 text-right">
                        {formatRupiah(selectedTransaction.total_amount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Payment Information */}
            <div className="mb-4 border rounded-md p-3 bg-gray-50">
              <div className="text-sm text-gray-500 mb-1">Metode Pembayaran</div>
              <div className="font-medium">{selectedTransaction.payment_method}</div>
              <div className="mt-2 text-sm">
                <span className="text-gray-500">No. Rekening:</span> {selectedTransaction.payment_account}
              </div>
              <div className="text-sm">
                <span className="text-gray-500">Atas Nama:</span> {selectedTransaction.payment_account_name}
              </div>
              {selectedTransaction.payment_notes && (
                <div className="mt-2 text-sm text-gray-500">
                  <span className="font-medium">Catatan:</span> {selectedTransaction.payment_notes}
                </div>
              )}
            </div>

            {/* Payment Status Information */}
            <div className="p-3 rounded bg-gray-50 mb-4">
              {selectedTransaction.status === 'approved' ? (
                <div>
                  <div className="text-sm text-gray-500">Diterima oleh</div>
                  <div className="font-medium">{selectedTransaction.approved_by}</div>
                  <div className="text-xs text-gray-500 mt-1">{selectedTransaction.approved_at}</div>
                  {selectedTransaction.admin_notes && (
                    <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-800">
                      <div className="font-medium">Catatan admin:</div>
                      <div>{selectedTransaction.admin_notes}</div>
                    </div>
                  )}
                </div>
              ) : selectedTransaction.status === 'rejected' ? (
                <div>
                  <div className="text-sm text-gray-500">Ditolak oleh</div>
                  <div className="font-medium">{selectedTransaction.rejected_by}</div>
                  <div className="text-xs text-gray-500 mt-1">{selectedTransaction.rejected_at}</div>
                  {selectedTransaction.rejection_reason && (
                    <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-800">
                      <div className="font-medium">Alasan penolakan:</div>
                      <div>{selectedTransaction.rejection_reason}</div>
                    </div>
                  )}
                  {selectedTransaction.admin_notes && (
                    <div className="mt-2 p-2 bg-gray-100 rounded text-sm text-gray-800">
                      <div className="font-medium">Catatan admin:</div>
                      <div>{selectedTransaction.admin_notes}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-yellow-600">
                  Menunggu konfirmasi admin
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 flex items-center"
              >
                <FiDownload className="mr-1" /> Cetak
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout userRole="staff" userName="Staff">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Riwayat Transaksi</h1>
        <p className="text-gray-600">Lihat dan kelola riwayat transaksi pembayaran</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col gap-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Cari ID transaksi atau nama pelanggan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Cari
            </button>
          </form>

          <div className="flex flex-wrap gap-4">
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status Verifikasi</label>
              <div className="relative">
                <select
                  value={filterStatus}
                  onChange={handleFilterChange}
                  className="block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                >
                  <option value="all">Semua</option>
                  <option value="approved">Diterima</option>
                  <option value="rejected">Ditolak</option>
                </select>
                <FiFilter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status Pembayaran</label>
              <div className="relative">
                <select
                  value={filterPaymentStatus}
                  onChange={handlePaymentStatusChange}
                  className="block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                >
                  <option value="all">Semua</option>
                  <option value="paid">Lunas</option>
                  <option value="unpaid">Belum Bayar</option>
                  <option value="partial">Bayar Sebagian</option>
                </select>
                <FiFilter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Dari Tanggal</label>
              <div className="relative">
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={handleDateFromChange}
                  className="block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FiCalendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Sampai Tanggal</label>
              <div className="relative">
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={handleDateToChange}
                  className="block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FiCalendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="ml-auto self-end">
              <button
                onClick={handleExportData}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
              >
                <FiDownload className="mr-1" /> Export Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-20 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
            <p className="mt-2 text-gray-500">Memuat data...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-gray-500">Tidak ada data transaksi yang ditemukan</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pelanggan
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kendaraan
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tanggal
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Jumlah
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status Verifikasi
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status Pembayaran
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{transaction.invoice_number}</div>
                      {getBatchBadge(transaction) && (
                        <div className="mt-1">{getBatchBadge(transaction)}</div>
                      )}
                      <div className="text-xs text-gray-500">#{transaction.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{transaction.customer_name}</div>
                      <div className="text-xs text-gray-500">{transaction.customer_phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{transaction.vehicle_make} {transaction.vehicle_model}</div>
                      <div className="text-xs text-gray-500">{transaction.vehicle_plate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{transaction.service_date}</div>
                      <div className="text-xs text-gray-400">Pembayaran: {transaction.payment_date}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div>
                        <div>{formatRupiah(transaction.total_amount)}</div>
                        <div className="text-xs font-normal text-gray-500">
                          {getServiceCount(transaction)} layanan
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(transaction.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentStatusBadge(transaction.payment_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleViewDetail(transaction)}
                        className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full font-medium text-sm transition"
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && transactions.length > 0 && renderPagination()}

      {/* Detail Modal */}
      {showDetailModal && renderDetailModal()}
    </DashboardLayout>
  );
};

export default StaffRiwayatTransaksi; 
